import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Cake, MapPin, Clock, UserCircle, QrCode, CheckCircle, Warning, Fire, Confetti } from "@phosphor-icons/react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const InvitePage = () => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [myQr, setMyQr] = useState(null);

  useEffect(() => {
    fetchInvite();
  }, [inviteCode]);

  const fetchInvite = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/invite/${inviteCode}`);
      setInvite(res.data);
    } catch {
      setInvite(null);
    } finally {
      setLoading(false);
    }
  };

  const acceptInvite = async () => {
    if (!user) {
      toast.info("Faça cadastro primeiro para aceitar o convite!");
      navigate(`/login?tab=register&redirect=/convite/${inviteCode}`);
      return;
    }
    setAccepting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/invite/${inviteCode}/accept`, {}, {
        headers: getAuthHeaders(), withCredentials: true,
      });
      setMyQr({ qr_code: res.data.qr_code, ticket_code: res.data.ticket_code });
      toast.success("Convite aceito! Seu QR Code foi gerado.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao aceitar convite");
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Cake size={48} weight="duotone" className="text-[#FF5A26] animate-pulse" />
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4">
        <div className="text-center">
          <Warning size={64} weight="duotone" className="text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#042F2E] mb-2">Convite não encontrado</h1>
          <p className="text-[#115E59] mb-6">Este convite pode ter sido cancelado ou não existe.</p>
          <Button onClick={() => navigate("/")} className="bg-[#FF5A26] hover:bg-[#E04818] text-white rounded-full px-8">
            Ir para o MARABU
          </Button>
        </div>
      </div>
    );
  }

  const isFesta = invite.party_type === "festa";
  const dateFormatted = new Date(invite.date + "T12:00:00").toLocaleDateString("pt-BR", {
    weekday: "long", day: "numeric", month: "long", year: "numeric"
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#042F2E] to-[#0D3D3C]" data-testid="invite-page">
      {/* Decorative top */}
      <div className="relative overflow-hidden pt-12 pb-8 px-4 text-center">
        <div className="absolute inset-0 opacity-10">
          {Array.from({ length: 20 }).map((_, i) => (
            <Confetti key={i} size={24} weight="fill" className="text-white absolute"
              style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, transform: `rotate(${Math.random() * 360}deg)` }} />
          ))}
        </div>
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200 }}>
          <div className="w-20 h-20 bg-[#FF5A26] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl">
            {isFesta ? <Confetti size={40} weight="fill" className="text-white" /> : <Fire size={40} weight="fill" className="text-white" />}
          </div>
        </motion.div>
        <p className="text-white/60 text-sm uppercase tracking-widest font-bold mb-2">Você está convidado(a)</p>
        <h1 className="text-3xl sm:text-4xl font-black text-white">
          {isFesta ? "Festa de Aniversário" : "Churrasqueira"}
        </h1>
      </div>

      {/* Card */}
      <div className="px-4 pb-10 -mt-2">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-2xl max-w-md mx-auto overflow-hidden"
        >
          {/* Host info */}
          <div className="bg-[#F4F1EA] p-6 text-center border-b border-[#E4DFD3]">
            <p className="text-sm text-[#115E59] mb-1">Anfitrião</p>
            <p className="text-2xl font-black text-[#042F2E]" data-testid="invite-host">{invite.host_name}</p>
          </div>

          {/* Details */}
          <div className="p-6 space-y-5">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 bg-[#FFF4E6] rounded-xl flex items-center justify-center shrink-0">
                <Cake size={22} weight="fill" className="text-[#FF5A26]" />
              </div>
              <div>
                <p className="text-xs font-bold text-[#115E59] uppercase tracking-wider">Data</p>
                <p className="text-[#042F2E] font-bold capitalize" data-testid="invite-date">{dateFormatted}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-11 h-11 bg-[#FFF4E6] rounded-xl flex items-center justify-center shrink-0">
                <Clock size={22} weight="fill" className="text-[#FF5A26]" />
              </div>
              <div>
                <p className="text-xs font-bold text-[#115E59] uppercase tracking-wider">Horário</p>
                <p className="text-[#042F2E] font-bold">{invite.time_start} às {invite.time_end}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-11 h-11 bg-[#FFF4E6] rounded-xl flex items-center justify-center shrink-0">
                <MapPin size={22} weight="fill" className="text-[#FF5A26]" />
              </div>
              <div>
                <p className="text-xs font-bold text-[#115E59] uppercase tracking-wider">Local</p>
                <p className="text-[#042F2E] font-bold" data-testid="invite-location">{invite.location}</p>
              </div>
            </div>

            {invite.booking_notes && (
              <div className="bg-[#F4F1EA] rounded-xl p-4 text-sm text-[#115E59]">
                <p className="font-bold text-[#042F2E] mb-1">Observações:</p>
                {invite.booking_notes}
              </div>
            )}
          </div>

          {/* Action area */}
          <div className="p-6 border-t border-[#E4DFD3]">
            {myQr ? (
              /* QR Code generated */
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center">
                <CheckCircle size={48} weight="fill" className="text-[#10B981] mx-auto mb-3" />
                <p className="text-xl font-bold text-[#042F2E] mb-1">Convite Aceito!</p>
                <p className="text-sm text-[#115E59] mb-5">Apresente este QR Code na entrada do MARABU</p>
                <div className="bg-white border-4 border-[#FF5A26] rounded-2xl p-4 inline-block mb-3">
                  <img src={`data:image/png;base64,${myQr.qr_code}`} alt="QR Code" className="w-52 h-52" data-testid="invite-qr" />
                </div>
                <p className="font-mono font-bold text-[#FF5A26] text-sm">{myQr.ticket_code}</p>
              </motion.div>
            ) : invite.status === "accepted" ? (
              <div className="text-center">
                <CheckCircle size={40} weight="fill" className="text-[#10B981] mx-auto mb-2" />
                <p className="font-bold text-[#042F2E]">Convite já aceito</p>
                <p className="text-sm text-[#115E59]">por {invite.guest_name}</p>
              </div>
            ) : invite.status === "cancelled" ? (
              <div className="text-center">
                <Warning size={40} weight="fill" className="text-red-400 mx-auto mb-2" />
                <p className="font-bold text-[#042F2E]">Convite cancelado</p>
              </div>
            ) : (
              /* Accept button — require login */
              <div>
                {user ? (
                  <>
                    <Button
                      onClick={acceptInvite}
                      disabled={accepting}
                      className="w-full bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full py-6 text-lg shadow-lg"
                      data-testid="accept-invite-btn"
                    >
                      {accepting ? "Aceitando..." : "Aceitar Convite e Gerar QR Code"}
                    </Button>
                    <p className="text-center text-xs text-[#115E59] mt-3">
                      Seu QR Code único será gerado ao aceitar
                    </p>
                  </>
                ) : (
                  <>
                    <div className="bg-[#FFF4E6] rounded-xl p-4 mb-4 text-center">
                      <p className="font-bold text-[#042F2E] mb-1">Baixe o app para aceitar o convite!</p>
                      <p className="text-xs text-[#115E59]">Cadastre-se no MARABU OPEN CLUB para garantir sua entrada grátis. Convidados sem cadastro pagam a entrada do clube.</p>
                    </div>
                    <Button
                      onClick={() => navigate(`/login?tab=register&redirect=/convite/${inviteCode}`)}
                      className="w-full bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full py-6 text-lg shadow-lg mb-3"
                      data-testid="register-invite-btn"
                    >
                      Cadastrar e Aceitar Convite
                    </Button>
                    <Button
                      onClick={() => navigate(`/login?redirect=/convite/${inviteCode}`)}
                      variant="outline"
                      className="w-full border-[#E4DFD3] text-[#042F2E] font-bold rounded-full py-5"
                      data-testid="login-invite-btn"
                    >
                      Já tenho conta — Entrar
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer branding */}
          <div className="bg-[#042F2E] p-4 text-center">
            <p className="text-white font-black text-sm">MARABU OPEN CLUB</p>
            <p className="text-white/50 text-xs">Rua Clarimundo de Melo, 197 - Encantado, Rio de Janeiro</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default InvitePage;
